import { AsyncAction, CommanderThunkAction, WithScope } from '@mews-commander/types';
import { Identifiable } from '@mews/std';
import { createAction, getBase64, lt, showSuccessAlert } from '@mews/framework';
import { ImageResult, AccountType } from '@mews-commander/data-access';
import { getWallClockDateUtcString, createFetchAction, createUpdateValue } from '@mews-commander/utils';
import { addCustomer } from '@mews-commander/legacy-entities/src/ducks/customers';
import { fetchAccountById, updateAccount } from '@mews-commander/legacy-entities/src/actions/accounts';
import {
    createAddressPagination,
    createFilesPagination,
    createIdentityDocumentsPagination,
} from '../customerDetail/actions/tabs';
import { refreshPage } from '../app/actions/common';
import {
    CUSTOMER_LOYALTY_IMAGE_DELETED,
    CUSTOMER_LOYALTY_IMAGE_UPDATED,
    CUSTOMER_PROFILE_INIT,
    CUSTOMER_PROFILE_UPDATE_FAILED,
    CUSTOMER_PROFILE_UPDATE_STARTED,
    CUSTOMER_PROFILE_UPDATE_SUCCEEDED,
} from './actionTypes';
import { getProfileFormInitialValues } from './selectors';
import { ProfileFormValues } from './types';

export const createCustomerProfile =
    (profileValues: $TSFixMe, enterpriseId: string): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch) => {
        const { birthDateString, ...otherProfileValues } = profileValues;
        const birthDateUtc = getWallClockDateUtcString(birthDateString);
        return dispatch(addCustomer({ ...otherProfileValues, enterpriseId, birthDateUtc })).then(
            ({ value: customer }) => customer
        );
    };

export const updateCustomerProfile =
    (id: string, values: ProfileFormValues): CommanderThunkAction<Promise<unknown>> =>
    async (dispatch, getState) => {
        dispatch(createAction(CUSTOMER_PROFILE_UPDATE_STARTED));

        const initialValues = getProfileFormInitialValues(getState(), { id });
        if (!initialValues) {
            return dispatch(createAction(CUSTOMER_PROFILE_UPDATE_FAILED));
        }

        const birthDateUpdateValue = createUpdateValue(initialValues.birthDate, values.birthDate);
        const birthDate = birthDateUpdateValue && { value: getWallClockDateUtcString(birthDateUpdateValue.value) };

        const accountUpdates = [
            {
                data: {
                    discriminator: AccountType.Customer,
                    value: {
                        id,
                        email: createUpdateValue(initialValues.email, values.email),
                        title: createUpdateValue(initialValues.titlePrefix, values.titlePrefix),
                        firstName: createUpdateValue(initialValues.firstName, values.firstName),
                        lastName: createUpdateValue(initialValues.lastName, values.lastName),
                        secondLastName: createUpdateValue(initialValues.secondLastName, values.secondLastName),
                        sex: createUpdateValue(initialValues.gender, values.gender),
                        nationalityCode: createUpdateValue(initialValues.nationality, values.nationality),
                        telephone: createUpdateValue(initialValues.telephone, values.telephone),
                        birthDate,
                        birthPlace: createUpdateValue(initialValues.birthPlace, values.birthPlace),
                        preferredLanguageCode: createUpdateValue(initialValues.language, values.language),
                        carRegistrationNumber: createUpdateValue(
                            initialValues.carRegistrationNumber,
                            values.carRegistrationNumber
                        ),
                        taxIdentifier: createUpdateValue(initialValues.taxIdentifier, values.taxIdentifier),
                        companyId: createUpdateValue(initialValues.companyFieldId, values.companyFieldId),
                        occupation: createUpdateValue(initialValues.occupation, values.occupation),
                        withdrawCardConsent: createUpdateValue(
                            initialValues.withdrawCardConsent,
                            values.withdrawCardConsent
                        ),
                    },
                },
            },
        ];
        await dispatch(updateAccount({ accountUpdates }))
            .then(() => {
                dispatch(refreshPage());
                showSuccessAlert({
                    description: lt('SaveSuccessMessage'),
                });
            })
            .catch(() => dispatch(createAction(CUSTOMER_PROFILE_UPDATE_FAILED)));

        return dispatch(createAction(CUSTOMER_PROFILE_UPDATE_SUCCEEDED));
    };

export const initCustomerProfile =
    ({ id, scope }: Identifiable & WithScope): CommanderThunkAction =>
    async (dispatch) => {
        const fetchActions = [
            dispatch(
                fetchAccountById({
                    accountId: id,
                    extent: {
                        addresses: true,
                        relatedCustomers: true,
                        files: true,
                        creditLimits: true,
                        legalIdentifiers: true,
                        profiles: true,
                        identityDocuments: true,
                    },
                })
            ).then(() => {
                dispatch(createAddressPagination(id, scope));
                dispatch(createIdentityDocumentsPagination(id, scope));
                dispatch(createFilesPagination(id, scope));
            }),
        ];

        const initChain = Promise.all(fetchActions);
        return dispatch(createFetchAction(CUSTOMER_PROFILE_INIT, initChain));
    };

export const updateLoyaltyImage =
    (customerId: string, image: File): CommanderThunkAction<AsyncAction<ImageResult>> =>
    async (dispatch, getState, { repositories }) => {
        const base64Data = (await getBase64(image)) || '';
        const promise = repositories.customers.updateLoyaltyImage({ customerId, base64Data }).then(({ imageId }) => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
            return {
                imageId,
            };
        });
        return dispatch(createFetchAction(CUSTOMER_LOYALTY_IMAGE_UPDATED, promise));
    };

export const deleteLoyaltyImage =
    (customerId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.customers.deleteLoyaltyImage({ customerId }).then(() => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });
        return dispatch(createFetchAction(CUSTOMER_LOYALTY_IMAGE_DELETED, promise));
    };
